import { ActionBar } from '@/modules/application';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Checkbox,
  FormControlLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
} from '@mui/material';
import { FunctionComponent, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { LocationFormModel } from './LocationFormModel';
import { LocationGeofenceEditor } from '../LocationGeofenceEditor';
import { CountrySelect } from '../CountrySelect';
import { GeocodedAddressSelectDialog } from '../GeocodedAddressSelectDialog';
import { createHexagon } from '@/modules/map/lib/geo.utils';
import { HexagonOutlined, InfoOutlined } from '@mui/icons-material';
import { ActionBarDeleteButton } from '@/modules/application/components/ActionBarDeleteButton';
import { locationService } from '../../api/locations/location.service';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { ApiResponseCode } from '@/lib/api/api-response-code';
import { LocationSelect } from '../LocationSelect';
import { Permission } from '@/modules/users/submodules/roles/api/permissions.contracts';
import { useUser } from '@/modules/users/contexts/UserContext';
import { mapService } from '@/modules/map/api/map.service';
import { GeocodeResultDTO } from '@/modules/map/api/map.contracts';
import { useTranslation } from '@/lib';
import { LocationType } from '../../api/locations/location.contracts';
import { isBetaTenant } from '@/config/tenant-features';
import { useAuthUserContext } from '@/modules/auth/context/AuthUserContext';

interface LocationFormDisplayProps {
  form: UseFormReturn<LocationFormModel>;
  onSubmit: (data: LocationFormModel) => void;
  isCreate?: boolean;
  locationId?: number;
}

// const geocodingClient = Geocoder(mapbox({ accessToken: MapboxToken }));

const LocationFormDisplay: FunctionComponent<LocationFormDisplayProps> = ({ form, onSubmit, isCreate = false, locationId }) => {
  const { formState, register, handleSubmit, getValues, control, watch, setValue } = form;
  const { errors, isSubmitting } = formState;
  const [isSelectGeocodedAddressDialogOpen, setIsSelectGeocodedAddressDialogOpen] = useState(false);
  const [geocodedAddresses, setGeocodedAddresses] = useState<GeocodeResultDTO[]>();
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const { t } = useTranslation();
  const { tenantId } = useAuthUserContext();
  const isSystemLocation = form.getValues('isSystem');

  async function createGeofenceFromAddress() {
    const currentFormValues = getValues();
    const geocodeResult = await mapService.forwardGeocode({
      address: {
        city: currentFormValues.city,
        country: currentFormValues.country,
        postalCode: currentFormValues.postalCode,
        streetName: currentFormValues.streetName,
        streetNumber: currentFormValues.streetNumber,
      },
    });

    if (geocodeResult.isSuccess) {
      setGeocodedAddresses(geocodeResult.payload);
      setIsSelectGeocodedAddressDialogOpen(true);
    }
  }

  function onGeocodedAddressSelectDialogClosed(selectedAddress?: GeocodeResultDTO, radiusInMeters?: number) {
    // Add geofence from gecodre result
    if (selectedAddress) {
      console.log({ selectedAddress });
      const hexagonalPolygon = createHexagon(selectedAddress.latitude, selectedAddress.longitude, radiusInMeters ?? 500);
      setValue('geofence', hexagonalPolygon);
    }
    setIsSelectGeocodedAddressDialogOpen(false);
  }

  async function deleteLocation() {
    if (locationId) {
      const response = await locationService.delete(locationId);
      if (!response.isSuccess) {
        if (response.responseCode === ApiResponseCode.LOCATION_HAS_ASSETS) {
          toast.error(t('delete_location_error_assets'), {
            autoClose: 10000,
          });
        } else if (response.responseCode === ApiResponseCode.LOCATION_IS_SYSTEM) {
          toast.error(t('delete_location_error_system'));
        } else if (response.responseCode === ApiResponseCode.LOCATION_HAS_CHILDREN) {
          toast(t('delete_location_error_children'));
        } else {
          toast.error(t('delete_location_error'));
        }
      } else {
        toast.success(t('delete_location_success'));
        navigate('/app/locations/overview');
      }
    }
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isSystemLocation && (
        <Alert severity="info" className="mb-4">
          {t('system_location_alert')}
          <Tooltip title={t('system_location_tooltip')}>
            <span className="font-semibold pl-1 underline decoration-dotted">{t('more_info')}</span>
          </Tooltip>
        </Alert>
      )}
      <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3 ">
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" className="pb-2" color="textSecondary">
              {t('details')}
            </Typography>
            <div className="flex w-full flex-col gap-y-2">
              <Controller
                name="parentLocationId"
                control={control}
                render={({ field: { onChange, onBlur, value, ref }, formState, fieldState }) => (
                  <LocationSelect
                    onlyEligibleParentsForLocationId={locationId}
                    exludedLocationIds={isCreate || !locationId ? [] : [locationId]}
                    helperText={errors.parentLocationId?.message}
                    label={t('parent_location')}
                    selectedLocationId={value !== null ? value : undefined}
                    disabled={isSystemLocation}
                    onSelected={(selectedLocation) => {
                      if (selectedLocation) {
                        onChange(selectedLocation.dto.id);
                      } else {
                        onChange(null);
                      }
                    }}
                  />
                )}
              />

              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.name !== undefined}
                    helperText={errors.name?.message}
                    label={t('name')}
                    {...field}
                    disabled={isSystemLocation}
                  />
                )}
              />

              <Controller
                name="code"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    error={errors.code !== undefined}
                    helperText={errors.code?.message}
                    label={t('code')}
                    {...field}
                    disabled={isSystemLocation}
                  />
                )}
              />

              <Controller
                name="description"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    error={errors.description !== undefined}
                    helperText={errors.description?.message}
                    label={t('description')}
                    {...field}
                    disabled={isSystemLocation}
                  />
                )}
              />

              <Controller
                name="externalId"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.externalId !== undefined}
                    helperText={errors.externalId?.message}
                    label={t('external_id')}
                    {...field}
                    disabled={isSystemLocation}
                  />
                )}
              />

              {isBetaTenant(tenantId) && (
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <FormControl>
                      <InputLabel id="location-type-select-label">{t('location_type')}</InputLabel>
                      <Select
                        labelId="location-type-select-label"
                        id="location-type-select"
                        label={t('location_type')}
                        {...field}
                        disabled={isSystemLocation}
                      >
                        <MenuItem value={LocationType.CARRIER}>{t('location_type_carrier')}</MenuItem>
                        <MenuItem value={LocationType.DEPO}>{t('location_type_depo')}</MenuItem>
                        <MenuItem value={LocationType.SYSTEM} disabled>
                          {t('location_type_system')}
                        </MenuItem>
                      </Select>
                      <FormHelperText>{errors.type?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              )}

              <Controller
                name="includeInReturnRate"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} disabled={isSystemLocation} />}
                    label={
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        {t('include_in_return_rate')}
                        <Tooltip title={t('include_in_return_rate_tooltip')} placement="bottom" arrow>
                          <InfoOutlined fontSize="small" style={{ marginLeft: 4 }} />
                        </Tooltip>
                      </span>
                    }
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" className="pb-2" color="textSecondary">
              {t('address')}
            </Typography>

            <div className={'flex flex-col gap-y-2'}>
              <Controller
                name="country"
                control={control}
                render={({ field: { onChange, onBlur, value, ref }, formState, fieldState }) => (
                  <>
                    <CountrySelect onChange={onChange} value={value} disabled={isSystemLocation} />
                  </>
                )}
              />
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.city !== undefined}
                    helperText={errors.city?.message}
                    label={t('city')}
                    {...field}
                    disabled={isSystemLocation}
                  />
                )}
              />
              <Controller
                name="streetName"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.streetName !== undefined}
                    helperText={errors.streetName?.message}
                    label={t('street_name')}
                    {...field}
                    disabled={isSystemLocation}
                  />
                )}
              />
              <Controller
                name="streetNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.streetNumber !== undefined}
                    helperText={errors.streetNumber?.message}
                    label={t('street_number')}
                    {...field}
                    disabled={isSystemLocation}
                  />
                )}
              />
              <Controller
                name="postalCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.postalCode !== undefined}
                    helperText={errors.postalCode?.message}
                    label={t('postal_code')}
                    {...field}
                    disabled={isSystemLocation}
                  />
                )}
              />
              <Stack spacing={2} direction="row">
                <Button
                  onClick={createGeofenceFromAddress}
                  variant="text"
                  startIcon={<HexagonOutlined className="rotate-90" />}
                  disabled={isSystemLocation}
                >
                  {t('create_geofence_from_address')}
                </Button>
              </Stack>
              {geocodedAddresses && (
                <GeocodedAddressSelectDialog
                  onClose={onGeocodedAddressSelectDialogClosed}
                  open={isSelectGeocodedAddressDialogOpen}
                  options={geocodedAddresses}
                />
              )}
            </div>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" className="pb-2" color="textSecondary">
              {t('geofence')}
            </Typography>

            <div className={'flex flex-col'}>
              {isSystemLocation ? (
                <Alert severity="info">{t('system_location_geofence_readonly')}</Alert>
              ) : (
                <Controller
                  name="geofence"
                  control={control}
                  render={({ field: { onChange, onBlur, value, ref }, formState, fieldState }) => (
                    <>
                      <LocationGeofenceEditor onPolygonDrawn={onChange} polygon={value ?? []} />
                    </>
                  )}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      {hasPermission(Permission.LOCATIONS_EDIT) && !isSystemLocation && (
        <ActionBar>
          <Button variant="contained" type="submit" loading={isSubmitting}>
            {t('save')}
          </Button>
          <ActionBarDeleteButton
            deleteConfirmationQuestion={t('delete_location_confirmation')}
            onDeleteConfirmed={deleteLocation}
            isVisible={!isCreate && !form.getValues('isSystem')}
            isLoading={isSubmitting}
          />
        </ActionBar>
      )}
    </form>
  );
};

export default LocationFormDisplay;
